import {
  Button,
  TextField,
  ErrorBanner,
  Badge,
  Tabs,
} from "frontend-kit/components/index.js";
import { getChainApp, getWallet } from "./chain-app.js";
import { duskToLux, luxToDusk, GAS_RESERVE_LUX } from "./chain-config.js";
import { isParticipantReady, loadParticipant } from "./participant-store.js";
import { loadTradingKey } from "./trading-key-store.js";
import {
  OP,
  encodeDepositPayload,
  encodeWithdrawPayload,
  signTraderAuth,
  buildTraderAuthArg,
  hexToBytes,
  base58ToBytes,
} from "./trader-crypto.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { duskAmountNode } from "./dusk-amount.js";
import { withNonceRetry } from "./trader-nonce.js";
import { isAutoRetryEnabled } from "./trade-settings.js";

const FUNDS_TIP =
  "Deposit moves DUSK from your Moonlight wallet into contract collateral (wallet shows the amount). Withdraw sends it back. Trades spend this balance and only show gas in the wallet.";

function tipBtn(text) {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "tip-btn";
  b.textContent = "?";
  b.title = text;
  b.setAttribute("aria-label", text);
  b.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const existing = b.parentElement?.querySelector(".tip-pop");
    if (existing) {
      existing.remove();
      return;
    }
    document.querySelectorAll(".tip-pop").forEach((el) => el.remove());
    const pop = document.createElement("div");
    pop.className = "tip-pop";
    pop.setAttribute("role", "tooltip");
    pop.textContent = text;
    const host = b.parentElement;
    if (host) {
      if (getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      host.append(pop);
    }
    const close = (ev) => {
      if (ev.target === b || pop.contains(ev.target)) return;
      pop.remove();
      document.removeEventListener("click", close);
    };
    setTimeout(() => document.addEventListener("click", close), 0);
  });
  return b;
}

function requireReady() {
  if (!isParticipantReady()) {
    throw new Error("Finish participant onboarding first (wallet icon)");
  }
  const key = loadTradingKey();
  if (!key?.sk_hex) throw new Error("Connect a trading key in Account settings");
  const participant = loadParticipant();
  if (
    participant?.trading_commitment_hex &&
    key.commitment_hex &&
    participant.trading_commitment_hex.toLowerCase() !== key.commitment_hex.toLowerCase()
  ) {
    throw new Error("Trading key does not match linked participant commitment");
  }
  return { key, participant };
}

/**
 * Deposit / withdraw free collateral.
 * @param {{ log?: Function, onDone?: Function, initialBalance?: bigint|number|string, initialTab?: "deposit"|"withdraw" }} opts
 */
export function renderDepositPanel({
  log,
  onDone,
  initialBalance = null,
  initialTab = "deposit",
} = {}) {
  const root = document.createElement("div");
  root.className = "funds-panel funds-panel--popover dt-card";

  const head = document.createElement("div");
  head.className = "funds-head";
  const labelRow = document.createElement("div");
  labelRow.className = "funds-label-row";
  const label = document.createElement("span");
  label.className = "stat-label";
  label.textContent = "Free collateral";
  labelRow.append(label, tipBtn(FUNDS_TIP));
  head.append(labelRow);
  root.append(head);

  let freeLux = initialBalance != null ? BigInt(initialBalance) : null;
  let walletLux = null;
  /** @type {"dusk" | "lux"} */
  let amountUnit = "dusk";
  let tab = initialTab === "withdraw" ? "withdraw" : "deposit";
  let busy = false;

  /** @type {HTMLInputElement | null} */
  let amountInput = null;
  /** @type {HTMLInputElement | null} */
  let freeInput = null;

  const tabsHost = document.createElement("div");
  tabsHost.className = "funds-tabs-host";
  root.append(tabsHost);

  const body = document.createElement("div");
  body.className = "funds-body";
  root.append(body);

  const feedback = document.createElement("div");
  feedback.className = "funds-feedback";
  root.append(feedback);

  /** Source balance for the active tab: wallet's own Moonlight balance on
   * Deposit (what you actually have to send), free collateral on Withdraw
   * (what the contract already holds for you). */
  function sourceLux() {
    return tab === "withdraw" ? freeLux : walletLux;
  }

  /** "Max" amount for the active tab. Deposit reserves GAS_RESERVE_LUX out of
   * the wallet balance so the tx that spends it still has gas to pay for
   * itself - otherwise "Max" would drain the wallet to a balance that can't
   * even submit the deposit. Withdraw has no such reserve: withdrawn funds
   * come from contract collateral, not the wallet, so they don't compete
   * with gas for the tx that withdraws them. */
  function maxAmountLux() {
    const lux = sourceLux();
    if (lux == null) return null;
    if (tab === "withdraw") return lux;
    const capped = lux - GAS_RESERVE_LUX;
    return capped > 0n ? capped : 0n;
  }

  function formatLux(lux) {
    if (lux == null) return "-";
    return amountUnit === "lux" ? String(lux) : String(luxToDusk(lux));
  }

  function paintFreeField() {
    if (freeInput) freeInput.value = formatLux(sourceLux());
  }

  async function refreshBalance() {
    try {
      const key = loadTradingKey();
      if (!key?.commitment_hex) return;
      const dusk = await getChainApp();
      const bal = await dusk.readContract({
        contract: "predictionMarket",
        functionName: "collateral_of",
        args: Array.from(hexToBytes(key.commitment_hex)),
      });
      freeLux = BigInt(bal);
      paintFreeField();
    } catch {
      /* keep last known */
    }
  }
  if (initialBalance == null) void refreshBalance();

  async function refreshWalletBalance() {
    try {
      const wallet = getWallet();
      if (!wallet?.state?.selectedAddress) return;
      const { value } = await wallet.getPublicBalance();
      walletLux = BigInt(value);
      paintFreeField();
    } catch {
      /* keep last known */
    }
  }
  void refreshWalletBalance();

  function clearFeedback() {
    feedback.replaceChildren();
  }

  function showSuccess(message) {
    const box = document.createElement("div");
    box.className = "funds-success";
    box.append(Badge({ variant: "success", label: "Success" }));
    const p = document.createElement("p");
    p.append(message);
    box.append(p);
    feedback.replaceChildren(box);
  }

  function showError(message) {
    feedback.replaceChildren(ErrorBanner({ message }));
  }

  function amountToInputValue(lux) {
    if (lux == null || lux <= 0n) return amountUnit === "lux" ? "0" : "0";
    return amountUnit === "lux" ? String(lux) : String(luxToDusk(lux));
  }

  function parseAmountLux() {
    const raw = Number(amountInput?.value);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    if (amountUnit === "lux") return BigInt(Math.round(raw));
    return duskToLux(raw);
  }

  function switchUnit(next) {
    if (next === amountUnit) return;
    const lux = parseAmountLux();
    amountUnit = next;
    paintForm();
    if (lux != null && amountInput) {
      amountInput.value = amountToInputValue(lux);
    }
  }

  function paintTabs() {
    tabsHost.replaceChildren(
      Tabs({
        tabs: [
          { id: "deposit", label: "Deposit" },
          { id: "withdraw", label: "Withdraw" },
        ],
        activeId: tab,
        onChange: (id) => {
          tab = id;
          clearFeedback();
          paintTabs();
          paintForm();
          if (id === "deposit") void refreshWalletBalance();
        },
      })
    );
  }

  function paintForm() {
    body.replaceChildren();

    const freeRow = document.createElement("div");
    freeRow.className = "funds-free-row";

    const freeLabel = tab === "withdraw" ? "Free" : "Moonlight";
    const freeField = TextField({
      id: "funds-free",
      label: `${freeLabel} (${amountUnit.toUpperCase()})`,
      value: formatLux(sourceLux()),
    });
    freeField.classList.add("funds-free-field");
    freeInput = freeField.querySelector("input");
    if (freeInput) {
      freeInput.readOnly = true;
      freeInput.disabled = true;
      freeInput.tabIndex = -1;
      freeInput.classList.add("is-readonly");
      freeInput.setAttribute("aria-readonly", "true");
      freeInput.title =
        tab === "withdraw"
          ? "Free collateral available to trade"
          : "DUSK balance in your connected Moonlight wallet";
    }
    freeRow.append(freeField);

    freeRow.append(
      Button({
        label: tab === "withdraw" ? "Max free" : "Max",
        disabled: busy || maxAmountLux() == null || maxAmountLux() <= 0n,
        onClick: () => {
          const lux = maxAmountLux();
          if (lux == null || lux <= 0n || !amountInput) return;
          amountInput.value = amountToInputValue(lux);
        },
      })
    );
    body.append(freeRow);

    if (tab === "deposit") {
      const gasHint = document.createElement("p");
      gasHint.className = "hint funds-gas-hint";
      gasHint.textContent = `Max leaves ${luxToDusk(GAS_RESERVE_LUX)} DUSK in your wallet for gas.`;
      body.append(gasHint);
    }

    const amountRow = document.createElement("div");
    amountRow.className = "funds-amount-row";

    const field = TextField({
      id: "funds-amount",
      label: `Amount (${amountUnit.toUpperCase()})`,
      value: amountInput?.value ?? (amountUnit === "lux" ? "100000000" : "0.1"),
    });
    amountInput = field.querySelector("input");
    amountRow.append(field);

    const unit = document.createElement("div");
    unit.className = "unit-toggle";
    unit.setAttribute("role", "group");
    unit.setAttribute("aria-label", "Amount unit");
    for (const u of ["dusk", "lux"]) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `unit-toggle-btn${amountUnit === u ? " is-active" : ""}`;
      btn.textContent = u.toUpperCase();
      btn.disabled = busy;
      btn.addEventListener("click", () => switchUnit(u));
      unit.append(btn);
    }
    amountRow.append(unit);
    body.append(amountRow);

    const actions = document.createElement("div");
    actions.className = "funds-actions";
    actions.append(
      Button({
        label: busy
          ? "Working…"
          : tab === "deposit"
            ? "Deposit"
            : "Withdraw",
        variant: "primary",
        disabled: busy,
        onClick: () => runFunds(tab),
      })
    );
    body.append(actions);
  }

  async function withAuth(op, payloadBytes, nonce) {
    const { key } = requireReady();
    const { signatureHex } = await signTraderAuth({
      skHex: key.sk_hex,
      op,
      commitmentHex: key.commitment_hex,
      nonce,
      payloadBytes,
    });
    return buildTraderAuthArg({
      pkBase58: key.pk_base58,
      nonce,
      signatureHex,
    });
  }

  async function runFunds(kind) {
    clearFeedback();
    let ui = null;
    try {
      const { participant } = requireReady();
      const luxBi = parseAmountLux();
      if (luxBi == null || luxBi <= 0n) throw new Error("Amount must be > 0");
      if (kind === "withdraw" && freeLux != null && luxBi > freeLux) {
        throw new Error(
          `Insufficient free collateral (have ${luxToDusk(freeLux)} DUSK)`
        );
      }
      const lux = Number(luxBi);
      busy = true;
      paintForm();
      const txLabel = document.createDocumentFragment();
      txLabel.append(kind === "deposit" ? "Deposit " : "Withdraw ", duskAmountNode(luxBi));
      ui = showGlobalTx({ label: txLabel });
      // Withdraw pays out to `recipient`, bound into the signed payload -
      // the connected wallet's own Moonlight account, not whichever
      // account happens to submit the transaction (see
      // `call_types::WithdrawArgs` doc comment in the contract).
      let recipient = null;
      if (kind === "withdraw") {
        recipient = getWallet()?.state?.selectedAddress ?? null;
        if (!recipient) throw new Error("Connect a wallet to withdraw");
      }
      const { key } = requireReady();
      const dusk = await getChainApp();
      const commitment = Array.from(hexToBytes(key.commitment_hex));
      const readChainNonce = async () =>
        Number(
          await dusk.readContract({
            contract: "predictionMarket",
            functionName: "next_nonce",
            args: commitment,
          })
        );

      async function attemptFunds(nonce) {
        const auth = await withAuth(
          kind === "deposit" ? OP.DEPOSIT : OP.WITHDRAW,
          kind === "deposit"
            ? encodeDepositPayload(lux)
            : encodeWithdrawPayload(lux, base58ToBytes(recipient)),
          nonce
        );
        const tx =
          kind === "deposit"
            ? await dusk.writeContract({
                contract: "predictionMarket",
                functionName: "deposit_collateral",
                args: { auth, amount: lux },
                privacy: "public",
                deposit: String(lux),
              })
            : await dusk.writeContract({
                contract: "predictionMarket",
                functionName: "withdraw_collateral",
                args: { auth, amount: lux, recipient },
                privacy: "public",
              });
        return trackTx(tx, { ui, autoDismissMs: 8000 });
      }

      // Nonce reserved through the shared per-commitment queue so a
      // deposit/withdraw here can't collide with a trade/claim submitted
      // from elsewhere on the page moments apart - see trader-nonce.js.
      // Retry on a residual (cross-tab/device) nonce race is opt-in via
      // Settings - see trade-settings.js.
      const tracked = await withNonceRetry(key.commitment_hex, readChainNonce, attemptFunds, {
        retryOnStaleNonce: isAutoRetryEnabled(),
        onRetrying: (e) => {
          ui.setStatus(
            "retrying",
            "That nonce was stale (another action beat it to the chain) - retrying with a fresh one…"
          );
          log?.(`${kind} rejected on a stale nonce (${e.message}) - retrying once with a fresh one`);
        },
      });
      await Promise.all([refreshBalance(), refreshWalletBalance()]);
      const verb = kind === "deposit" ? "Deposited" : "Withdrew";
      const successMsg = document.createDocumentFragment();
      successMsg.append(
        `${verb} `,
        duskAmountNode(luxBi),
        " · free collateral now ",
        duskAmountNode(freeLux ?? 0n)
      );
      showSuccess(successMsg);
      log?.(
        `${kind} tx ${tracked.hash} executed (participant ${participant.participant_id})`
      );
      onDone?.({ kind, hash: tracked.hash, amountLux: luxBi, balanceLux: freeLux });
    } catch (e) {
      const msg = e.message || String(e);
      showError(msg);
      log?.(`${kind} failed: ${msg}`);
      ui?.setStatus("failed", msg);
    } finally {
      busy = false;
      paintForm();
    }
  }

  paintTabs();
  paintForm();
  return root;
}

/** @deprecated use renderDepositPanel inside the header funds popover */
export function openFundsModal(opts = {}) {
  return renderDepositPanel(opts);
}
